// src/components/Admin/FirmPortalAnalytics.js
import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Paper, Tabs, Tab, Grid, CircularProgress, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, LinearProgress, TextField, Button,
    Card, CardContent, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions,
    FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    People as PeopleIcon,
    QuestionAnswer as QuestionIcon,
    Business as BusinessIcon,
    TrendingDown as TrendingDownIcon,
    AccessTime as TimeIcon,
    Article as ArticleIcon,
    Description as DescriptionIcon,
    Download as DownloadIcon,
    Share as ShareIcon
} from '@mui/icons-material';
import { db } from '../../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
    ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { toast } from 'react-toastify';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table as DocxTable, TableRow as DocxTableRow, TableCell as DocxTableCell, WidthType } from 'docx';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import ShareModal from '../ShareModal';
import FirmIndustryInsightsCard from './FirmIndustryInsightsCard';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

// Helper function for safe date parsing
const getCreatedAt = (item) => {
    if (!item?.createdAt) return null; // Return null if missing, don't fake it as "Today"
    if (typeof item.createdAt.toDate === 'function') {
        return item.createdAt.toDate();
    }
    if (item.createdAt instanceof Date) {
        return item.createdAt;
    }
    return new Date(item.createdAt);
};

const FirmPortalAnalytics = ({ user, orgData, orgConfig }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Date range state - default to last 30 days
    const getDefaultDateFrom = () => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date.toISOString().split('T')[0];
    };
    const getDefaultDateTo = () => {
        return new Date().toISOString().split('T')[0];
    };
    const [dateFrom, setDateFrom] = useState(getDefaultDateFrom);
    const [dateTo, setDateTo] = useState(getDefaultDateTo);
    
    // Data state
    const [questions, setQuestions] = useState([]);
    const [businessUpdates, setBusinessUpdates] = useState([]);
    const [clients, setClients] = useState([]);
    const [members, setMembers] = useState([]);
    
    // Export state
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [exportFormat, setExportFormat] = useState('csv');
    
    // Share state
    const [shareModalOpen, setShareModalOpen] = useState(false);

    // Fetch all data
    useEffect(() => {
        const fetchData = async () => {
            if (!user?.organizationId) {
                setError("No organization ID found.");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                // Fetch questions
                const questionsQuery = query(
                    collection(db, 'questions'),
                    where('organizationId', '==', user.organizationId),
                    orderBy('createdAt', 'desc')
                );
                const questionsSnap = await getDocs(questionsQuery);
                const questionList = questionsSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setQuestions(questionList);

                // Fetch businessUpdates (SavvaiSignals)
                const updatesQuery = query(
                    collection(db, 'businessUpdates'),
                    where('organizationId', '==', user.organizationId),
                    orderBy('createdAt', 'desc')
                );
                const updatesSnap = await getDocs(updatesQuery);
                const updatesList = updatesSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setBusinessUpdates(updatesList);

                // Fetch clients
                const clientsQuery = query(
                    collection(db, 'clients'),
                    where('organizationId', '==', user.organizationId)
                );
                const clientsSnap = await getDocs(clientsQuery);
                const clientList = clientsSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setClients(clientList);

                // Fetch members (Users)
                const membersQuery = query(
                    collection(db, 'users'),
                    where('organizationId', '==', user.organizationId)
                );
                const membersSnap = await getDocs(membersQuery);
                const memberList = membersSnap.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(m => m.userPortalAccess !== false);
                setMembers(memberList);

                setLoading(false);
            } catch (err) {
                console.error('Error fetching analytics data:', err);
                setError(`Failed to load analytics: ${err.message}`);
                setLoading(false);
            }
        };

        fetchData();
    }, [user?.organizationId]);

    // Filter questions by date range
    const filteredQuestions = useMemo(() => {
        // Parse inputs as Local Time to match user expectation
        const fromDate = dateFrom ? new Date(dateFrom + 'T00:00:00') : null;
        const toDate = dateTo ? new Date(dateTo + 'T23:59:59') : null;
        
        return questions.filter(q => {
            const createdAt = getCreatedAt(q);
            if (!createdAt) return false; // Filter out items with no date
            
            if (fromDate && createdAt < fromDate) return false;
            if (toDate && createdAt > toDate) return false;
            return true;
        });
    }, [questions, dateFrom, dateTo]);

    // Filter businessUpdates by date range
    const filteredUpdates = useMemo(() => {
        const fromDate = dateFrom ? new Date(dateFrom + 'T00:00:00') : null;
        const toDate = dateTo ? new Date(dateTo + 'T23:59:59') : null;
        
        return businessUpdates.filter(u => {
            const createdAt = getCreatedAt(u);
            if (!createdAt) return false;
            
            if (fromDate && createdAt < fromDate) return false;
            if (toDate && createdAt > toDate) return false;
            return true;
        });
    }, [businessUpdates, dateFrom, dateTo]);

    // ==================== FIRM ANALYTICS CALCULATIONS ====================

    // Summary stats
    const summaryStats = useMemo(() => {
        const activeClients = clients.filter(c => c.isActive !== false).length;
        const totalQuestions = filteredQuestions.length;
        const avgQuestionsPerClient = activeClients > 0 
            ? (totalQuestions / activeClients).toFixed(1) 
            : 0;

        // Count SavvaiSignals in date range
        const totalSignals = filteredUpdates.length;

        // Count Decision Memos from followUps in both questions and businessUpdates
        let decisionMemoCount = 0;
        
        // Check questions for decision memos in followUps
        filteredQuestions.forEach(q => {
            if (q.followUps && Array.isArray(q.followUps)) {
                decisionMemoCount += q.followUps.filter(f => f.type === 'decision_memo').length;
            }
        });
        
        // Check businessUpdates for decision memos in followUps
        filteredUpdates.forEach(u => {
            if (u.followUps && Array.isArray(u.followUps)) {
                decisionMemoCount += u.followUps.filter(f => f.type === 'decision_memo').length;
            }
        });

        return {
            totalClients: clients.length,
            activeClients,
            totalQuestions,
            avgQuestionsPerClient,
            totalSignals,
            decisionMemoCount
        };
    }, [clients, filteredQuestions, filteredUpdates]);

    // Question trends over time (daily for date range)
    const questionTrends = useMemo(() => {
        const days = [];
        // Use Local Time construction to match filteredQuestions logic
        const fromDate = dateFrom ? new Date(dateFrom + 'T00:00:00') : new Date();
        const toDate = dateTo ? new Date(dateTo + 'T00:00:00') : new Date();
        
        // Calculate number of days in range
        const diffTime = Math.abs(toDate - fromDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        
        for (let i = 0; i < diffDays; i++) {
            const date = new Date(fromDate);
            date.setDate(date.getDate() + i);
            date.setHours(0, 0, 0, 0);
            days.push({
                date: date.toISOString().split('T')[0],
                label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                count: 0
            });
        }

        filteredQuestions.forEach(q => {
            const createdAt = getCreatedAt(q);
            const dateStr = createdAt.toISOString().split('T')[0];
            const day = days.find(d => d.date === dateStr);
            if (day) day.count++;
        });

        // For longer ranges (>30 days), aggregate by week
        if (diffDays > 30) {
            const weeks = [];
            for (let i = 0; i < days.length; i += 7) {
                const weekDays = days.slice(i, i + 7);
                const total = weekDays.reduce((sum, d) => sum + d.count, 0);
                weeks.push({
                    label: weekDays[0]?.label || '',
                    count: total
                });
            }
            return weeks;
        }

        return days;
    }, [filteredQuestions, dateFrom, dateTo]);

    // Industry distribution
    const industryDistribution = useMemo(() => {
        const industryCounts = {};
        
        clients.forEach(c => {
            const industry = c.industryCanonical || c.industry || 'Not Specified';
            industryCounts[industry] = (industryCounts[industry] || 0) + 1;
        });

        return Object.entries(industryCounts)
            .map(([industry, count]) => ({ name: industry, value: count }))
            .sort((a, b) => b.value - a.value);
    }, [clients]);

    // ==================== USER PERFORMANCE CALCULATIONS ====================

    const fcfoPerformance = useMemo(() => {
        return members
            .filter(m => m.userType === 'FCFO' || m.userPortalAccess === true)
            .map(fcfo => {
                const fcfoClients = clients.filter(c => c.fcfoId === fcfo.id);
                const activeClients = fcfoClients.filter(c => c.isActive !== false);
                const fcfoQuestions = filteredQuestions.filter(q => q.fcfoId === fcfo.id);
                const fcfoQuestionsAllTime = questions.filter(q => q.fcfoId === fcfo.id);
                
                // Questions in selected date range (replaces hardcoded "last 30 days")
                const questionsInRange = fcfoQuestions.length;

                // Calculate inactive rate
                const inactiveThreshold = 30; // days
                const inactiveClients = fcfoClients.filter(c => {
                    if (c.isActive === false) return false; // Don't count archived
                    const lastActivity = c.lastActivityAt?.toDate?.() || c.createdAt?.toDate?.();
                    if (!lastActivity) return true;
                    const daysSince = Math.floor((new Date() - lastActivity) / (1000 * 60 * 60 * 24));
                    return daysSince > inactiveThreshold;
                });

                const inactiveRate = activeClients.length > 0 
                    ? Math.round((inactiveClients.length / activeClients.length) * 100)
                    : 0;

                return {
                    id: fcfo.id,
                    name: fcfo.name || fcfo.email || 'Unknown',
                    email: fcfo.email,
                    totalClients: fcfoClients.length,
                    activeClients: activeClients.length,
                    totalQuestions: fcfoQuestionsAllTime.length,
                    questionsInRange: questionsInRange,
                    inactiveClients: inactiveClients.length,
                    inactiveRate,
                    avgQuestionsPerClient: activeClients.length > 0 
                        ? (fcfoQuestionsAllTime.length / activeClients.length).toFixed(1)
                        : 0
                };
            })
            .sort((a, b) => b.totalClients - a.totalClients);
    }, [members, clients, questions, filteredQuestions]);

    // ==================== EXPORT FUNCTIONS ====================
    
    // Generate CSV content for analytics
    const generateAnalyticsCSV = () => {
        const rows = [];
        
        rows.push(['Savvai Firm Analytics Export']);
        rows.push([`Date Range: ${dateFrom} to ${dateTo}`]);
        rows.push([`Export Date: ${new Date().toLocaleDateString()}`]);
        rows.push([]);
        
        rows.push(['=== SUMMARY STATISTICS ===']);
        rows.push(['Metric', 'Value']);
        rows.push(['Total Clients', summaryStats.totalClients]);
        rows.push(['Active Clients', summaryStats.activeClients]);
        rows.push(['Questions (in date range)', summaryStats.totalQuestions]);
        rows.push(['Avg Questions per Client', summaryStats.avgQuestionsPerClient]);
        rows.push(['SavvaiSignals (in date range)', summaryStats.totalSignals]);
        rows.push(['Decision Memos (in date range)', summaryStats.decisionMemoCount]);
        rows.push([]);
        
        rows.push(['=== QUESTION VOLUME OVER TIME ===']);
        rows.push(['Date', 'Questions']);
        questionTrends.forEach(trend => {
            rows.push([trend.label, trend.count]);
        });
        rows.push([]);
        
        rows.push(['=== CLIENTS BY INDUSTRY ===']);
        rows.push(['Industry', 'Count']);
        industryDistribution.forEach(ind => {
            rows.push([ind.name, ind.value]);
        });
        rows.push([]);
        
        rows.push(['=== USER ACTIVITY ===']);
        rows.push(['User', 'Email', 'Clients', 'Active Clients', 'Total Questions', 'Questions (in range)', 'Avg Q/Client', 'Inactive Rate']);
        fcfoPerformance.forEach(u => {
            rows.push([u.name, u.email, u.totalClients, u.activeClients, u.totalQuestions, u.questionsInRange, u.avgQuestionsPerClient, `${u.inactiveRate}%`]);
        });
        
        return rows.map(row => 
            row.map(cell => `"${(cell?.toString() || '').replace(/"/g, '""')}"`).join(',')
        ).join('\n');
    };
    
    // Generate Word document for analytics
    const generateAnalyticsWord = async () => {
        const children = [];
        
        children.push(new Paragraph({ text: "Savvai Firm Analytics Report", heading: HeadingLevel.TITLE, spacing: { after: 200 } }));
        children.push(new Paragraph({ children: [new TextRun({ text: "Date Range: ", bold: true }), new TextRun({ text: `${dateFrom} to ${dateTo}` })], spacing: { after: 100 } }));
        children.push(new Paragraph({ children: [new TextRun({ text: "Export Date: ", bold: true }), new TextRun({ text: new Date().toLocaleDateString() })], spacing: { after: 300 } }));
        
        children.push(new Paragraph({ text: "Summary Statistics", heading: HeadingLevel.HEADING_1, spacing: { before: 200, after: 150 } }));
        
        const summaryTable = new DocxTable({
            rows: [
                new DocxTableRow({ children: [
                    new DocxTableCell({ children: [new Paragraph({ text: "Metric" })], shading: { fill: 'E8E8E8' } }),
                    new DocxTableCell({ children: [new Paragraph({ text: "Value" })], shading: { fill: 'E8E8E8' } })
                ]}),
                new DocxTableRow({ children: [new DocxTableCell({ children: [new Paragraph({ text: "Total Clients" })] }), new DocxTableCell({ children: [new Paragraph({ text: summaryStats.totalClients.toString() })] })] }),
                new DocxTableRow({ children: [new DocxTableCell({ children: [new Paragraph({ text: "Active Clients" })] }), new DocxTableCell({ children: [new Paragraph({ text: summaryStats.activeClients.toString() })] })] }),
                new DocxTableRow({ children: [new DocxTableCell({ children: [new Paragraph({ text: "Questions (in date range)" })] }), new DocxTableCell({ children: [new Paragraph({ text: summaryStats.totalQuestions.toString() })] })] }),
                new DocxTableRow({ children: [new DocxTableCell({ children: [new Paragraph({ text: "Avg Questions per Client" })] }), new DocxTableCell({ children: [new Paragraph({ text: summaryStats.avgQuestionsPerClient.toString() })] })] }),
                new DocxTableRow({ children: [new DocxTableCell({ children: [new Paragraph({ text: "SavvaiSignals (in date range)" })] }), new DocxTableCell({ children: [new Paragraph({ text: summaryStats.totalSignals.toString() })] })] }),
                new DocxTableRow({ children: [new DocxTableCell({ children: [new Paragraph({ text: "Decision Memos (in date range)" })] }), new DocxTableCell({ children: [new Paragraph({ text: summaryStats.decisionMemoCount.toString() })] })] })
            ],
            width: { size: 100, type: WidthType.PERCENTAGE }
        });
        children.push(summaryTable);
        
        children.push(new Paragraph({ text: "Question Volume Over Time", heading: HeadingLevel.HEADING_1, spacing: { before: 400, after: 150 } }));
        const trendRows = [new DocxTableRow({ children: [new DocxTableCell({ children: [new Paragraph({ text: "Date" })], shading: { fill: 'E8E8E8' } }), new DocxTableCell({ children: [new Paragraph({ text: "Questions" })], shading: { fill: 'E8E8E8' } })] })];
        questionTrends.forEach(trend => { trendRows.push(new DocxTableRow({ children: [new DocxTableCell({ children: [new Paragraph({ text: trend.label })] }), new DocxTableCell({ children: [new Paragraph({ text: trend.count.toString() })] })] })); });
        children.push(new DocxTable({ rows: trendRows, width: { size: 100, type: WidthType.PERCENTAGE } }));
        
        children.push(new Paragraph({ text: "Clients by Industry", heading: HeadingLevel.HEADING_1, spacing: { before: 400, after: 150 } }));
        const industryRows = [new DocxTableRow({ children: [new DocxTableCell({ children: [new Paragraph({ text: "Industry" })], shading: { fill: 'E8E8E8' } }), new DocxTableCell({ children: [new Paragraph({ text: "Count" })], shading: { fill: 'E8E8E8' } })] })];
        industryDistribution.forEach(ind => { industryRows.push(new DocxTableRow({ children: [new DocxTableCell({ children: [new Paragraph({ text: ind.name })] }), new DocxTableCell({ children: [new Paragraph({ text: ind.value.toString() })] })] })); });
        children.push(new DocxTable({ rows: industryRows, width: { size: 100, type: WidthType.PERCENTAGE } }));
        
        children.push(new Paragraph({ text: "User Activity", heading: HeadingLevel.HEADING_1, spacing: { before: 400, after: 150 } }));
        const userRows = [new DocxTableRow({ children: ['User', 'Clients', 'Active', 'Questions', 'In Range', 'Avg Q/Client', 'Inactive %'].map(h => new DocxTableCell({ children: [new Paragraph({ text: h })], shading: { fill: 'E8E8E8' } })) })];
        fcfoPerformance.forEach(u => { userRows.push(new DocxTableRow({ children: [u.name, u.totalClients, u.activeClients, u.totalQuestions, u.questionsInRange, u.avgQuestionsPerClient, `${u.inactiveRate}%`].map(v => new DocxTableCell({ children: [new Paragraph({ text: v.toString() })] })) })); });
        children.push(new DocxTable({ rows: userRows, width: { size: 100, type: WidthType.PERCENTAGE } }));
        
        return await Packer.toBlob(new Document({ sections: [{ children }] }));
    };
    
    // Handle export
    const handleExport = async () => {
        try {
            setIsExporting(true);
            const filenameBase = `Savvai-Analytics-${dateFrom}-to-${dateTo}`;
            let blob, filename;

            if (exportFormat === 'csv') {
                blob = new Blob([generateAnalyticsCSV()], { type: 'text/csv;charset=utf-8;' });
                filename = `${filenameBase}.csv`;
            } else {
                blob = await generateAnalyticsWord();
                filename = `${filenameBase}.docx`;
            }

            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
            toast.success(`Analytics exported to ${exportFormat.toUpperCase()} successfully!`);
            setExportModalOpen(false);
        } catch (error) {
            console.error('Export failed:', error);
            toast.error('Export failed. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };
    
    // Handle share
    const handleShare = async (shareData) => {
        try {
            const shareAnalyticsReportFn = httpsCallable(functions, 'shareAnalyticsReport');
            const result = await shareAnalyticsReportFn({
                recipientEmail: shareData.recipientEmail,
                recipientName: shareData.recipientName,
                personalMessage: shareData.customMessage || shareData.personalMessage || '',
                senderName: user?.displayName || user?.name || user?.email || 'Firm Admin',
                dateRange: { from: dateFrom, to: dateTo },
                summaryStats,
                questionTrends,
                industryDistribution,
                userActivity: fcfoPerformance
            });
            if (result.data.success) return result.data;
            throw new Error(result.data.error || 'Failed to share analytics report.');
        } catch (error) {
            console.error('Error sharing analytics report:', error);
            throw error;
        }
    };

    // ==================== RENDER HELPERS ====================

    const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary' }) => (
        <Card elevation={2}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="h3" color={`${color}.main`} fontWeight={600}>
                            {value}
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={500}>
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography variant="caption" color="text.secondary">
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    <Icon sx={{ fontSize: 40, color: `${color}.light`, opacity: 0.8 }} />
                </Box>
            </CardContent>
        </Card>
    );

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    // Clear date filters
    const clearDateFilters = () => {
        setDateFrom(getDefaultDateFrom());
        setDateTo(getDefaultDateTo());
    };

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Analytics
            </Typography>
            <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }} flexWrap="wrap">
                <Typography variant="body1" color="text.secondary">
                    Firm-wide analytics and performance metrics.
                </Typography>
                <TextField
                    label="Date From"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 160 }}
                />
                <TextField
                    label="Date To"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 160 }}
                />
                <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={clearDateFilters}
                >
                    Reset
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => setExportModalOpen(true)}
                >
                    Export
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ShareIcon />}
                    onClick={() => setShareModalOpen(true)}
                >
                    Share
                </Button>
            </Box>

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs 
                    value={activeTab} 
                    onChange={(e, v) => setActiveTab(v)}
                    variant="fullWidth"
                >
                    <Tab 
                        icon={<TrendingUpIcon />} 
                        label="Firm Analytics" 
                        iconPosition="start"
                    />
                    <Tab 
                        icon={<PeopleIcon />} 
                        label="User Activity" 
                        iconPosition="start"
                    />
                </Tabs>
            </Paper>

            {/* FIRM ANALYTICS TAB */}
            {activeTab === 0 && (
                <Box>
                   {/* Summary Cards */}
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={2}>
                            <StatCard
                                title="Total Clients"
                                value={summaryStats.totalClients}
                                subtitle={`${summaryStats.activeClients} active`}
                                icon={BusinessIcon}
                                color="primary"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <StatCard
                                title="Questions"
                                value={summaryStats.totalQuestions}
                                subtitle="In date range"
                                icon={QuestionIcon}
                                color="success"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <StatCard
                                title="Avg per Client"
                                value={summaryStats.avgQuestionsPerClient}
                                subtitle="Questions per active client"
                                icon={TrendingUpIcon}
                                color="info"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <StatCard
                                title="SavvaiSignals"
                                value={summaryStats.totalSignals}
                                subtitle="In date range"
                                icon={ArticleIcon}
                                color="secondary"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <StatCard
                                title="Decision Memos"
                                value={summaryStats.decisionMemoCount}
                                subtitle="In date range"
                                icon={DescriptionIcon}
                                color="warning"
                            />
                        </Grid>
                        {/* Industry Insights Card - moved here from bottom */}
                        {orgData?.firmPortalTier === 'enterprise' && (
                            <Grid item xs={12} sm={6} md={2}>
                                <FirmIndustryInsightsCard organizationId={user?.organizationId} />
                            </Grid>
                        )}
                    </Grid>

                    {/* Charts Row */}
                    <Box display="flex" gap={3} sx={{ mb: 4 }}>
                        {/* Question Trends */}
                        <Paper sx={{ p: 3, height: 400, flex: 1 }}>
                            <Typography variant="h6" gutterBottom>
                                Question Volume Over Time
                            </Typography>
                            <Box height={320}>
                                <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={0}>
                                        <LineChart data={questionTrends}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis 
                                            dataKey="label" 
                                            tick={{ fontSize: 12 }}
                                            interval="preserveStartEnd"
                                        />
                                        <YAxis allowDecimals={false} />
                                        <RechartsTooltip />
                                        <Line 
                                            type="monotone" 
                                            dataKey="count" 
                                            stroke="#1976d2" 
                                            strokeWidth={2}
                                            dot={{ r: 3 }}
                                            name="Questions"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Box>
                        </Paper>

                        {/* Industry Distribution */}
                        <Paper sx={{ p: 3, height: 400, flex: 1 }}>
                            <Typography variant="h6" gutterBottom>
                                Clients by Industry
                            </Typography>
                            <Box height={320}>
                                <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={0}>
                                        <PieChart>
                                        <Pie
                                            data={industryDistribution}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={50}
                                            outerRadius={85}
                                            paddingAngle={2}
                                            dataKey="value"
                                            label={({ name, percent }) => 
                                                `${name} (${(percent * 100).toFixed(0)}%)`
                                            }
                                            labelLine={{ length: 12, length2: 8 }}
                                        >
                                            {industryDistribution.map((entry, index) => (
                                                <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip formatter={(value, name) => [`${value} clients`, name]} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                        </Paper>
                    </Box>
                </Box>
            )}

            {/* User PERFORMANCE TAB */}
            {activeTab === 1 && (
                <Box>
                    {/* Summary */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard
                                title="Total Users"
                                value={fcfoPerformance.length}
                                subtitle="Active team members"
                                icon={PeopleIcon}
                                color="primary"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard
                                title="Avg Clients"
                                value={fcfoPerformance.length > 0 
                                    ? (clients.length / fcfoPerformance.length).toFixed(1)
                                    : 0
                                }
                                subtitle="Per User"
                                icon={BusinessIcon}
                                color="info"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                             <StatCard
                                title="Avg Questions"
                                value={fcfoPerformance.length > 0 
                                    ? Math.round(filteredQuestions.length / fcfoPerformance.length)
                                    : 0
                                }
                                subtitle={`Per User (${dateFrom} to ${dateTo})`}
                                icon={QuestionIcon}
                                color="success"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard
                                title="Avg Inactive Rate"
                                value={fcfoPerformance.length > 0 
                                    ? `${Math.round(fcfoPerformance.reduce((sum, f) => sum + f.inactiveRate, 0) / fcfoPerformance.length)}%`
                                    : '0%'
                                }
                                subtitle="Clients >30 days inactive"
                                icon={TimeIcon}
                                color="warning"
                            />
                        </Grid>
                    </Grid>

                    {/* Performance Table */}
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            User Activity Comparison
                        </Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                                        <TableCell>User</TableCell>
                                        <TableCell align="center">Clients</TableCell>
                                        <TableCell align="center">Active</TableCell>
                                        <TableCell align="center">Total Questions</TableCell>
                                        <TableCell align="center">In Date Range</TableCell>
                                        <TableCell align="center">Avg Q/Client</TableCell>
                                        <TableCell align="center">Inactive Rate</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {fcfoPerformance.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                <Typography color="text.secondary">
                                                    No User data available.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        fcfoPerformance.map(fcfo => (
                                            <TableRow key={fcfo.id} hover>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {fcfo.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {fcfo.email}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip label={fcfo.totalClients} size="small" />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip 
                                                        label={fcfo.activeClients} 
                                                        size="small" 
                                                        color="success" 
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    {fcfo.totalQuestions}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                                        {fcfo.questionsInRange}
                                                        {fcfo.questionsInRange > 5 && (
                                                            <TrendingUpIcon fontSize="small" color="success" />
                                                        )}
                                                        {fcfo.questionsInRange === 0 && (
                                                            <TrendingDownIcon fontSize="small" color="error" />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center">
                                                    {fcfo.avgQuestionsPerClient}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={fcfo.inactiveRate}
                                                            sx={{ 
                                                                width: 60, 
                                                                height: 8, 
                                                                borderRadius: 4,
                                                                bgcolor: 'grey.200',
                                                                '& .MuiLinearProgress-bar': {
                                                                    bgcolor: fcfo.inactiveRate > 50 ? 'error.main' 
                                                                        : fcfo.inactiveRate > 25 ? 'warning.main' 
                                                                        : 'success.main'
                                                                }
                                                            }}
                                                        />
                                                        <Typography variant="body2" color={
                                                            fcfo.inactiveRate > 50 ? 'error.main' 
                                                                : fcfo.inactiveRate > 25 ? 'warning.main' 
                                                                : 'success.main'
                                                        }>
                                                            {fcfo.inactiveRate}%
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    {/* Questions by User Chart */}
                    <Paper sx={{ p: 3, mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Questions by User (Selected Date Range)
                        </Typography>
                        <Box height={300}>
                            <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={0}>
                                    <BarChart data={fcfoPerformance}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="name" 
                                        tick={{ fontSize: 12 }}
                                        interval={0}
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                    />
                                    <YAxis allowDecimals={false} />
                                    <RechartsTooltip />
                                    <Bar dataKey="questionsInRange" fill="#1976d2" name="Questions (in range)" />
                                    <Bar dataKey="activeClients" fill="#4caf50" name="Active Clients" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </Paper>
               </Box>
            )}

            {/* Export Modal */}
            <Dialog open={exportModalOpen} onClose={() => setExportModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Export Analytics Report</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Export firm analytics data for the selected date range ({dateFrom} to {dateTo}).
                    </Typography>
                    <FormControl fullWidth>
                        <InputLabel>Export Format</InputLabel>
                        <Select value={exportFormat} label="Export Format" onChange={(e) => setExportFormat(e.target.value)}>
                            <MenuItem value="csv">CSV (.csv)</MenuItem>
                            <MenuItem value="word">Word Document (.docx)</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        The export will include: Summary statistics, Question volume over time, Clients by industry, and User activity.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExportModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleExport} variant="contained" disabled={isExporting} startIcon={isExporting ? <CircularProgress size={16} /> : <DownloadIcon />}>
                        {isExporting ? 'Exporting...' : `Export to ${exportFormat.toUpperCase()}`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Share Modal */}
            <ShareModal
                open={shareModalOpen}
                onClose={() => setShareModalOpen(false)}
                item={{ title: `Firm Analytics Report (${dateFrom} to ${dateTo})`, content: `Summary: ${summaryStats.totalClients} clients, ${summaryStats.totalQuestions} questions, ${summaryStats.totalSignals} signals` }}
                itemType="analytics_report"
                followUps={[]}
                onShare={handleShare}
                user={user}
            />
        </Box>
    );
};

export default FirmPortalAnalytics;// cache-bust 
